// pages/MemberDashboard.js - Updated version
import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import { toast } from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";

const MemberDashboard = () => {
  const { logout: logoutContext } = useContext(AuthContext);
  const [member, setMember] = useState(null);
  const [dashboardLocked, setDashboardLocked] = useState(false);
  const navigate = useNavigate();

  const fetchProfile = async () => {
    try {
      const res = await api.get("/profile");
      const memberData = res.data.member || res.data;

      setMember(memberData);
      setDashboardLocked(memberData.dashboardLocked);

      // Sync localStorage with backend truth
      localStorage.setItem("dashboardLocked", memberData.dashboardLocked);
      localStorage.setItem("memberEmail", memberData.email);
      localStorage.setItem("paymentStatus", memberData.paymentStatus);
      localStorage.setItem("registrationStatus", memberData.registrationStatus);
    } catch (error) {
      console.error("Error fetching member profile:", error);
      toast.error("Session expired. Please log in again.");
      logoutContext();
      localStorage.clear();
      navigate("/login", { replace: true });
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    fetchProfile();
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await api.post("/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("memberEmail");
      localStorage.removeItem("dashboardLocked");
      localStorage.removeItem("paymentStatus");
      localStorage.removeItem("registrationStatus");
      logoutContext();
      toast.success("Logged out successfully");
      navigate("/login", { replace: true });
    }
  };

  const handlePayment = () => {
    navigate("/payment-checkout");
  };

  const navigateToGolfStats = () => {
    navigate("/golf-statistics");
  };

  const navigateToProfile = () => {
    // You can implement profile page navigation here
    // toast.success("Profile page coming soon!");
    navigate("/profile");
  };

  const navigateToMembership = () => {
    // You can implement membership details page here
    // toast.success("Membership details coming soon!");
    navigate("/membership-details");
  };

  const navigateToEvents = () => {
    navigate("/upcoming-events");
  };

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-gray-600">
                Welcome back, {member.firstName || member.email.split("@")[0]}!
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {dashboardLocked && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-yellow-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Membership Payment Required
                </h3>
                <p className="text-gray-600 mb-6">
                  Your membership registration has been approved, but payment is
                  pending. Please complete the payment to unlock your dashboard
                  features.
                </p>
                <button
                  onClick={handlePayment}
                  className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition font-medium"
                >
                  Complete Membership Registration Payment
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Cards */}
        <div
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${
            dashboardLocked ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          {/* Profile Card */}
          <div
            onClick={dashboardLocked ? undefined : navigateToProfile}
            className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow ${
              dashboardLocked ? "cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Profile</h3>
            </div>
            <p className="text-gray-600">
              View and update your profile information
            </p>
          </div>

          {/* Membership Card */}
          <div
            onClick={dashboardLocked ? undefined : navigateToMembership}
            className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow ${
              dashboardLocked ? "cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Membership
              </h3>
            </div>
            <p className="text-gray-600">
              Check your membership status and details
            </p>
          </div>

          {/* Golf Statistics Card */}
          <div
            onClick={dashboardLocked ? undefined : navigateToGolfStats}
            className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow ${
              dashboardLocked ? "cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Golf Statistics
              </h3>
            </div>
            <p className="text-gray-600">
              Track and analyze your golf performance
            </p>
          </div>

          {/* Events Card */}
          <div
            onClick={dashboardLocked ? undefined : navigateToEvents}
            className={`bg-white rounded-lg shadow-sm p-6 cursor-pointer hover:shadow-md transition-shadow ${
              dashboardLocked ? "cursor-not-allowed" : ""
            }`}
          >
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                <svg
                  className="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Events</h3>
            </div>
            <p className="text-gray-600">
              Browse and register for upcoming events
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MemberDashboard;
